// Tracks whether a modal is currently present — either already open, OR
// triggered and in the middle of opening.
//
// The DOM alone is not enough. Modals like the bulk-download upsell open
// asynchronously: a click handler does a dynamic `import()`, React mounts the
// component, and only then does a `useEffect` call `dialog.showModal()` (which
// sets the `open` attribute) a frame later. So immediately after the user
// clicks there is a window where the modal is committed to opening but is not
// yet in the DOM.
//
// A deferred launcher — the full screen premium modal, shown after
// `await lcpFinalized()` — runs its check in a microtask right after that same
// click, i.e. squarely inside that window. Checking only the DOM there misses
// the opening modal and the two stack.
//
// Contract:
//   - A modal trigger calls `markModalOpening()` SYNCHRONOUSLY, before its async
//     import, and invokes the returned release fn when the modal closes (and on
//     failure, so the flag can't get stuck).
//   - A deferred launcher calls `isModalPresent()` before showing.
let openingCount = 0;

export function markModalOpening(): () => void {
  openingCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    openingCount = Math.max(0, openingCount - 1);
  };
}

// True if a modal is open or mid-open right now. "Mid-open" = a trigger called
// markModalOpening() synchronously but the dialog isn't in the DOM yet (dynamic
// import + React effect). The DOM check alone would miss that window.
export function isModalPresent(): boolean {
  return (
    openingCount > 0 ||
    document.querySelector('dialog.ds2-5-modal[open]') != null
  );
}

// Invoke `callback` as soon as no modal is present — immediately if nothing is
// open, otherwise as soon as the in-flight modal flow finishes (no breathing
// room). Used to QUEUE a page-load interruption (the full screen premium modal)
// behind a user-initiated modal flow so they show one at a time instead of
// overlapping. Polls because the flow opens follow-up modals asynchronously
// (e.g. reader-message after the bulk-download modal closes); each step keeps
// presence marked, so the gaps between them don't read as empty.
export function whenNoModalPresent(callback: () => void): void {
  const POLL_MS = 150;
  const check = (): void => {
    if (!isModalPresent()) {
      callback();
      return;
    }
    window.setTimeout(check, POLL_MS);
  };
  // Initial setTimeout lets the triggering click's dispatch fully settle before
  // the first check (browsers run microtask checkpoints between event listeners,
  // so a modal opened by that same click may not be marked yet).
  window.setTimeout(check, 0);
}
